import { Injectable, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';

export interface AuthCredentials {
  shopId: number;
  userId: number;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  // Store credentials in memory only (not localStorage for security)
  private _shopId = signal<number | null>(null);
  private _userId = signal<number | null>(null);
  private _token = signal<string | null>(null);
  private _initialized = signal<boolean>(false);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly shopId = this._shopId.asReadonly();
  readonly userId = this._userId.asReadonly();
  readonly token = this._token.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed: check if all credentials are valid
  readonly isAuthenticated = computed(() => {
    return this._shopId() !== null &&
           this._userId() !== null &&
           this._token() !== null &&
           this._shopId()! > 0 &&
           this._userId()! > 0 &&
           this._token()!.length > 0;
  });

  // Computed: get credentials object
  readonly credentials = computed<AuthCredentials | null>(() => {
    if (!this.isAuthenticated()) return null;
    return {
      shopId: this._shopId()!,
      userId: this._userId()!,
      token: this._token()!
    };
  });

  constructor() {
    // Set token synchronously from current URL so the auth interceptor has it on first request
    this.initializeFromRouterSnapshot();
    // Also subscribe to query param changes (e.g. if user navigates with new params)
    this.initializeFromQueryParams();
  }

  /**
   * Read credentials from the current router snapshot synchronously.
   * This ensures the Bearer token is available before any HTTP request is made.
   */
  private initializeFromRouterSnapshot(): void {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const params = route.snapshot.queryParams;
    this.applyParams(params);
  }

  /**
   * Initialize credentials from URL query parameters (async, for later updates).
   * Expected URL format: ?shopId=123&userId=456&token=eyJhbGc...
   */
  private initializeFromQueryParams(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      this.applyParams(params);
    });
  }

  /**
   * Parse and apply query params; set credentials or error.
   */
  private applyParams(params: Record<string, string | undefined>): void {
    const shopIdStr = params['shopId'];
    const userIdStr = params['userId'];
    const token = params['token'];

    // Validate and parse shopId
    const shopId = shopIdStr ? parseInt(shopIdStr, 10) : null;
    if (shopIdStr && (isNaN(shopId!) || shopId! <= 0)) {
      this._error.set('Invalid shopId parameter');
      this._initialized.set(true);
      return;
    }

    // Validate and parse userId
    const userId = userIdStr ? parseInt(userIdStr, 10) : null;
    if (userIdStr && (isNaN(userId!) || userId! <= 0)) {
      this._error.set('Invalid userId parameter');
      this._initialized.set(true);
      return;
    }

    // Check for missing required params
    if (!shopId || !userId || !token) {
      const missing: string[] = [];
      if (!shopId) missing.push('shopId');
      if (!userId) missing.push('userId');
      if (!token) missing.push('token');
      this._error.set(`Missing required parameters: ${missing.join(', ')}`);
      this._initialized.set(true);
      return;
    }

    // All params valid, set credentials
    this._shopId.set(shopId);
    this._userId.set(userId);
    this._token.set(token);
    this._error.set(null);
    this._initialized.set(true);

    console.log('Auth credentials initialized:', { shopId, userId, tokenLength: token.length });
  }

  /**
   * Manually set credentials (useful for testing or alternative auth flows)
   */
  setCredentials(shopId: number, userId: number, token: string): void {
    this._shopId.set(shopId);
    this._userId.set(userId);
    this._token.set(token);
    this._error.set(null);
    this._initialized.set(true);
  }

  /**
   * Clear all credentials (e.g., on logout or error)
   */
  clearCredentials(): void {
    this._shopId.set(null);
    this._userId.set(null);
    this._token.set(null);
    this._initialized.set(false);
  }

  /**
   * Get the Authorization header value
   */
  getAuthorizationHeader(): string | null {
    const token = this._token();
    return token ? `Bearer ${token}` : null;
  }
}
